const TAU = Math.PI * 2
const NEWTON_ITERATIONS = 8
const BISECTION_ITERATIONS = 24
const SPEED_STEP = 0.001
const CORNER_SAMPLES = 48
const SQUIRCLE_PARAMETER = 2
const CUBIC_BEZIER_PATTERN =
  /^cubic-bezier\(\s*([^,\s]+)\s*,\s*([^,\s]+)\s*,\s*([^,\s]+)\s*,\s*([^,\s)]+)\s*\)$/

/** Samples along the trail, from its tail to the head of the beam. */
export const TRAIL_SAMPLES = 70

export const TAIL_BASE_SHARE = 0.03

/** How much longer the tail grows with the beam's speed, as a share of the perimeter at a speed of 1. */
const TAIL_SPEED_SHARE = 0.09

/** The exponent that makes the trail glow brighter toward the head. */
export const TRAIL_CURVE = 1.8

/** The exponent of the core line, which stays brighter along the trail than the glow does. */
export const CORE_CURVE = 1.4

const FADE_IN_SHARE = 0.06

export const FADE_OUT_SHARE = 0.12

/** Share of the edge glow over which it rises; it falls over the rest. */
const GLOW_RISE_SHARE = 0.3

/** Segments around the perimeter that carry the edge glow's colour from the top edge to the bottom. */
export const GLOW_SEGMENTS = 180

export const HEAD_RADIUS = 6

/** One band of the beam's bloom: how far it lies outside the edge, how wide it is, which swatch colours it and which alpha of the paint it takes. */
export interface BloomBand {
  readonly offset: number
  readonly width: number
  readonly colour: 'bloom' | 'halo'
  readonly alpha: 'bloomOuter' | 'bloomInner' | 'bloomEdge'
}

/** The beam's bloom, drawn on its own layer and blurred once, from the widest band to the edge. */
export const BLOOM_BANDS: readonly BloomBand[] = [
  { offset: 1.5, width: 4, colour: 'bloom', alpha: 'bloomOuter' },
  { offset: -0.5, width: 3, colour: 'bloom', alpha: 'bloomInner' },
  { offset: 0, width: 2, colour: 'halo', alpha: 'bloomEdge' },
]

/** How far the beam's halo fringes lie either side of the core line. */
export const FRINGE_OFFSET = 0.5

export const FRINGE_WIDTH = 0.4

export const FRINGE_ALPHA = 0.65

export const CORE_WIDTH = 0.6

/** How far inside the tile edge the beam's bloom is erased. */
export const BEAM_ERASE_INSET = 3

export const GLOW_BLOOM_OFFSET = 1

export const GLOW_BLOOM_WIDTH = 2.5

export const GLOW_LINE_WIDTH = 0.6

export const GLOW_LINE_ALPHA = 0.55

/** How far inside the tile edge the edge glow's bloom is erased. */
export const GLOW_ERASE_INSET = 2

export const MAX_PIXEL_RATIO = 2

/** The paint of each beam layer: the alpha of its bands, which differs by theme. */
export interface BeamPaint {
  readonly bloomOuter: number
  readonly bloomInner: number
  readonly bloomEdge: number
  readonly head: number
  readonly core: number
  readonly glowBloom: number
}

export const BEAM_PAINT: Readonly<Record<'light' | 'dark', BeamPaint>> = {
  dark: {
    bloomOuter: 0.5,
    bloomInner: 0.45,
    bloomEdge: 0.45,
    head: 0.4,
    core: 0.85,
    glowBloom: 0.35,
  },
  light: {
    bloomOuter: 0.3,
    bloomInner: 0.25,
    bloomEdge: 0.15,
    head: 0.2,
    core: 0.8,
    glowBloom: 0.22,
  },
}

/** A progress in [0, 1] mapped to an eased progress in [0, 1]. */
export type EasingFunction = (progress: number) => number

/** One point on the path, with the unit normal pointing out of the shape. */
export interface PathPoint {
  readonly x: number
  readonly y: number
  readonly normalX: number
  readonly normalY: number
}

/** A closed path that runs clockwise from the top-left corner. */
export interface BeamPath {
  /** The length of the path, in CSS pixels. */
  readonly perimeter: number
  /** The point a distance along the path; a distance past the end wraps around. */
  pointAt(distance: number): PathPoint
  /** The corner points of the closed outline, in order: each straight edge by its start, each corner by dense samples. */
  outline(): PathPoint[]
}

/** The rectangle the path runs round, in canvas CSS pixels. */
export interface PathRect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

/** Where the beam's head is and how it is travelling, at one moment of the lap. */
export interface BeamHead {
  readonly distance: number
  readonly tail: number
}

/** One sample of the trail: where it is, and how far along it runs from tail (0) to head (1). */
export interface TrailSample {
  readonly point: PathPoint
  readonly position: number
}

/** One straight piece of a band, with the alpha it is drawn at. */
export interface BandSegment {
  readonly fromX: number
  readonly fromY: number
  readonly toX: number
  readonly toY: number
  readonly alpha: number
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value))
}

/** The CSS `cubic-bezier(x1, y1, x2, y2)` easing as a function of progress. */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): EasingFunction {
  const coordinate = (first: number, second: number, parameter: number): number => {
    const inverse = 1 - parameter
    return (
      3 * inverse * inverse * parameter * first +
      3 * inverse * parameter * parameter * second +
      parameter * parameter * parameter
    )
  }
  const slope = (first: number, second: number, parameter: number): number => {
    const inverse = 1 - parameter
    return (
      3 * inverse * inverse * first +
      6 * inverse * parameter * (second - first) +
      3 * parameter * parameter * (1 - second)
    )
  }

  const parameterAt = (progress: number): number => {
    let parameter = progress
    for (let iteration = 0; iteration < NEWTON_ITERATIONS; iteration += 1) {
      const error = coordinate(x1, x2, parameter) - progress
      const gradient = slope(x1, x2, parameter)
      if (Math.abs(error) < 1e-6) return parameter
      if (Math.abs(gradient) < 1e-6) break
      parameter -= error / gradient
    }
    let low = 0
    let high = 1
    parameter = progress
    for (let iteration = 0; iteration < BISECTION_ITERATIONS; iteration += 1) {
      if (coordinate(x1, x2, parameter) < progress) low = parameter
      else high = parameter
      parameter = (low + high) / 2
    }
    return parameter
  }

  return (progress) => {
    if (progress <= 0) return 0
    if (progress >= 1) return 1
    return coordinate(y1, y2, parameterAt(progress))
  }
}

/** Reads a `cubic-bezier(...)` token value into its easing, or `null` when the value is anything else. */
export function parseCubicBezier(token: string): EasingFunction | null {
  const match = CUBIC_BEZIER_PATTERN.exec(token.trim())
  if (match === null) return null
  const [x1, y1, x2, y2] = match.slice(1).map(Number)
  if ([x1, y1, x2, y2].some((value) => value === undefined || !Number.isFinite(value))) return null
  return cubicBezier(x1!, y1!, x2!, y2!)
}

interface PathPiece {
  readonly length: number
  at(distance: number): PathPoint
}

function straight(
  length: number,
  startX: number,
  startY: number,
  directionX: number,
  directionY: number,
): PathPiece {
  return {
    length,
    at: (distance) => ({
      x: startX + directionX * distance,
      y: startY + directionY * distance,
      normalX: directionY,
      normalY: -directionX,
    }),
  }
}

function arc(radius: number, centerX: number, centerY: number, startAngle: number): PathPiece {
  return {
    length: (Math.PI * radius) / 2,
    at: (distance) => {
      const angle = startAngle + distance / radius
      return {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
        normalX: Math.cos(angle),
        normalY: Math.sin(angle),
      }
    },
  }
}

/** The superellipse parameter `k` of a computed `corner-shape` value: the first of its values, and 1 (a circle) when it names no superellipse. */
export function cornerShapeParameter(value: string): number {
  const first = value.trim().split(/\s+(?![^(]*\))/)[0] ?? ''
  if (first === 'squircle') return SQUIRCLE_PARAMETER
  const match = /^superellipse\(\s*([^)\s]+)\s*\)$/.exec(first)
  const parameter = match === null ? Number.NaN : Number(match[1])
  return Number.isFinite(parameter) ? parameter : 1
}

function superellipseCorner(
  radius: number,
  centerX: number,
  centerY: number,
  quarter: number,
  parameter: number,
): PathPiece {
  const exponent = 2 ** parameter
  const root = 2 / exponent
  const samples = Array.from({ length: CORNER_SAMPLES + 1 }, (_, index) => {
    const angle = ((index / CORNER_SAMPLES) * Math.PI) / 2
    let unitX = Math.sin(angle) ** root
    let unitY = -(Math.cos(angle) ** root)
    for (let turn = 0; turn < quarter; turn += 1) [unitX, unitY] = [-unitY, unitX]
    const gradient = (unit: number) =>
      Math.abs(unit) < 1e-12 ? 0 : Math.sign(unit) * Math.abs(unit) ** (exponent - 1)
    const normalX = gradient(unitX)
    const normalY = gradient(unitY)
    const size = Math.hypot(normalX, normalY)
    return {
      x: centerX + radius * unitX,
      y: centerY + radius * unitY,
      normalX: normalX / size,
      normalY: normalY / size,
    }
  })
  const reach = [0]
  for (let index = 1; index < samples.length; index += 1) {
    const from = samples[index - 1]!
    const to = samples[index]!
    reach.push(reach[index - 1]! + Math.hypot(to.x - from.x, to.y - from.y))
  }
  const length = reach[reach.length - 1]!

  return {
    length,
    at: (distance) => {
      let index = 1
      while (index < reach.length - 1 && reach[index]! < distance) index += 1
      const from = samples[index - 1]!
      const to = samples[index]!
      const span = reach[index]! - reach[index - 1]!
      const share = span > 0 ? clamp01((distance - reach[index - 1]!) / span) : 0
      const normalX = from.normalX + (to.normalX - from.normalX) * share
      const normalY = from.normalY + (to.normalY - from.normalY) * share
      const size = Math.hypot(normalX, normalY)
      return {
        x: from.x + (to.x - from.x) * share,
        y: from.y + (to.y - from.y) * share,
        normalX: normalX / size,
        normalY: normalY / size,
      }
    },
  }
}

function cornerPiece(
  radius: number,
  centerX: number,
  centerY: number,
  quarter: number,
  parameter: number,
): PathPiece {
  if (parameter === 1) return arc(radius, centerX, centerY, -TAU / 4 + (quarter * TAU) / 4)
  return superellipseCorner(radius, centerX, centerY, quarter, parameter)
}

/** The rounded rectangle's edge, clockwise, starting where the top edge begins after the top-left corner; `cornerShape` is the corners' superellipse parameter, where 1 is a circle. */
export function roundedRectPath(rect: PathRect, radius: number, cornerShape = 1): BeamPath {
  const { x, y, width, height } = rect
  const corner = Math.min(Math.max(radius, 0), width / 2, height / 2)
  const right = x + width
  const bottom = y + height

  const pieces: PathPiece[] = [
    straight(width - 2 * corner, x + corner, y, 1, 0),
    cornerPiece(corner, right - corner, y + corner, 0, cornerShape),
    straight(height - 2 * corner, right, y + corner, 0, 1),
    cornerPiece(corner, right - corner, bottom - corner, 1, cornerShape),
    straight(width - 2 * corner, right - corner, bottom, -1, 0),
    cornerPiece(corner, x + corner, bottom - corner, 2, cornerShape),
    straight(height - 2 * corner, x, bottom - corner, 0, -1),
    cornerPiece(corner, x + corner, y + corner, 3, cornerShape),
  ]
  const perimeter = pieces.reduce((total, piece) => total + piece.length, 0)

  function pointAt(distance: number): PathPoint {
    let remaining = ((distance % perimeter) + perimeter) % perimeter
    for (const piece of pieces) {
      if (remaining <= piece.length) return piece.at(remaining)
      remaining -= piece.length
    }
    return pieces[0]!.at(0)
  }

  function outline(): PathPoint[] {
    return pieces.flatMap((piece, index) =>
      index % 2 === 0
        ? [piece.at(0)]
        : Array.from({ length: CORNER_SAMPLES }, (_, sample) =>
            piece.at((sample / CORNER_SAMPLES) * piece.length),
          ),
    )
  }

  return { perimeter, pointAt, outline }
}

/** How fast the eased progress moves at `progress`, where 1 is the average speed of the whole lap. */
export function beamSpeed(easing: EasingFunction, progress: number): number {
  const low = Math.max(0, progress - SPEED_STEP)
  const high = Math.min(1, progress + SPEED_STEP)
  return (easing(high) - easing(low)) / (high - low)
}

/** Where the head is at a progress of the lap, and how long a tail it drags: longer at speed, never longer than the way it has come. */
export function beamHead(easing: EasingFunction, progress: number, perimeter: number): BeamHead {
  const distance = easing(clamp01(progress)) * perimeter
  const speed = beamSpeed(easing, clamp01(progress))
  const tail = Math.min(perimeter * (TAIL_BASE_SHARE + TAIL_SPEED_SHARE * speed), distance)
  return { distance, tail }
}

/** The trail as `samples` points from its tail to the head, which is the last. */
export function beamTrail(
  path: BeamPath,
  head: BeamHead,
  samples: number = TRAIL_SAMPLES,
): TrailSample[] {
  return Array.from({ length: samples }, (_, index) => {
    const position = index / (samples - 1)
    return { point: path.pointAt(head.distance - head.tail * (1 - position)), position }
  })
}

/** How visible the beam is at a progress of its lap, which runs past 1 while the beam fades out: in over the first share, out over the share after the end. */
export function beamEnvelope(progress: number): number {
  const fadeIn = clamp01(progress / FADE_IN_SHARE)
  const fadeOut = clamp01(1 - (progress - 1) / FADE_OUT_SHARE)
  return fadeIn * fadeOut
}

/** How bright the edge glow is at a progress of its beat: it rises over the first share and falls over the rest. */
export function edgeGlowEnvelope(progress: number): number {
  if (progress <= 0 || progress >= 1) return 0
  if (progress < GLOW_RISE_SHARE) return progress / GLOW_RISE_SHARE
  return 1 - (progress - GLOW_RISE_SHARE) / (1 - GLOW_RISE_SHARE)
}

/** The path's points pushed `offset` along their outward normals; a negative offset lies inside the shape. */
export function offsetPoints(points: readonly PathPoint[], offset: number): PathPoint[] {
  return points.map((point) => ({
    ...point,
    x: point.x + point.normalX * offset,
    y: point.y + point.normalY * offset,
  }))
}

/** The straight pieces between consecutive points, each drawn at the alpha `alphaAt` gives its index. */
export function bandSegments(
  points: readonly PathPoint[],
  alphaAt: (index: number) => number,
): BandSegment[] {
  return points.slice(1).map((to, index) => {
    const from = points[index]!
    return { fromX: from.x, fromY: from.y, toX: to.x, toY: to.y, alpha: alphaAt(index) }
  })
}

/** How far down the rectangle each segment between consecutive points lies, from 0 at its top edge to 1 at its bottom edge. */
export function verticalWeights(points: readonly PathPoint[], rect: PathRect): number[] {
  return points.slice(1).map((to, index) => {
    const middle = (points[index]!.y + to.y) / 2
    return clamp01((middle - rect.y) / rect.height)
  })
}
